import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Wrench, Clock, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const RepairPage = () => {
  const commonProblems = [
    { problem: "Door won't open or close", solution: "Opener repair, track alignment, or spring adjustment" },
    { problem: "Door moves unevenly", solution: "Track realignment, roller replacement, or cable repair" },
    { problem: "Loud grinding noises", solution: "Roller replacement, lubrication, or worn part replacement" },
    { problem: "Door comes off track", solution: "Track repair, roller replacement, bracket adjustment" },
    { problem: "Remote not working", solution: "Opener programming, receiver repair, remote replacement" },
    { problem: "Door closes then reopens", solution: "Safety sensor adjustment, limit setting, track cleaning" }
  ];

  const repairProcess = [
    { step: 1, title: "Diagnostic Inspection", desc: "Thorough examination of your garage door system to identify all issues" },
    { step: 2, title: "Detailed Quote", desc: "Clear explanation of needed repairs with transparent pricing" },
    { step: 3, title: "Professional Repair", desc: "Expert technicians fix issues using quality galvanized steel parts" },
    { step: 4, title: "Testing & Warranty", desc: "Complete system test and 90-day warranty on all repairs" }
  ];

  const warrantyInfo = [
    "90-day warranty on all repair work",
    "Warranty on replacement parts (manufacturer dependent)",
    "Labor warranty covers workmanship",
    "Hardware warranty on galvanized steel components",
    "Free follow-up service if issue returns"
  ];

  const faqItems = [
    {
      q: "How quickly can you repair my garage door in Saskatoon?",
      a: "Most garage door repairs in Saskatoon are completed the same day. We carry common parts on our service trucks including high-cycle springs, galvanized steel cables, rollers, and hardware. Emergency repairs are available 24/7 for Saskatoon homeowners."
    },
    {
      q: "What's included in your repair warranty?",
      a: "All repairs come with a 90-day labor warranty covering our workmanship. Replacement parts carry their own manufacturer warranties. We use galvanized steel components for superior durability in Saskatoon's climate. If the same issue returns within the warranty period, we fix it free."
    },
    {
      q: "Do you repair all brands of garage doors?",
      a: "Yes! Our Saskatoon technicians service all major brands including Clopay, Amarr, Wayne Dalton, CHI, and more. We repair both residential and commercial garage door systems. Our trucks stock universal parts that work with most systems."
    },
    {
      q: "Should I repair or replace my garage door?",
      a: "For Saskatoon homeowners, we recommend repair when the door structure is sound and repair costs are reasonable. If your door has multiple major issues, is very old, or repair costs approach 50% of replacement cost, a new door may be more cost-effective. We provide honest recommendations."
    },
    {
      q: "What parts do you use for repairs?",
      a: "We use high-quality parts including high-cycle springs (rated for 25,000+ cycles), galvanized steel cables and hardware for rust resistance, nylon rollers for quiet operation, and commercial-grade components. All parts are sourced from reputable manufacturers serving Saskatoon professionals."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Garage Door Repair Saskatoon
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Professional repair services for residential and commercial garage doors in Saskatoon. Same-day service available with 90-day warranty on all work. We use galvanized steel parts and high-cycle springs for lasting repairs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg">
                <Phone size={22} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-lg">
                <MessageSquare size={22} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Professional Garage Door Repair in Saskatoon</h2>
              <p>
                When your garage door isn't working properly, it disrupts your daily routine and compromises your Saskatoon home's security. At 306 Garage Doors, we provide fast, reliable repair services for both residential and commercial properties throughout Saskatoon.
              </p>
              <p>
                Our experienced technicians diagnose and fix all types of garage door problems. From broken springs and frayed cables to malfunctioning openers and damaged panels, we handle every repair with professional expertise. We arrive with fully stocked service trucks containing high-cycle springs, galvanized steel cables, nylon rollers, and other quality components.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mt-8">Quality Parts & Materials</h3>
              <p>
                We use only premium parts for lasting repairs in Saskatoon's climate. Our standard includes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>High-Cycle Springs:</strong> Rated for 25,000+ cycles compared to standard 10,000-cycle springs</li>
                <li><strong>Galvanized Steel Cables:</strong> Rust-resistant for Saskatoon's weather conditions</li>
                <li><strong>Galvanized Hardware:</strong> Brackets, hinges, and mounting hardware with corrosion protection</li>
                <li><strong>Nylon Rollers:</strong> Quiet operation and longer lifespan than steel rollers</li>
                <li><strong>Commercial-Grade Components:</strong> Professional quality parts that last</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8">Residential & Commercial Repair Services</h3>
              <p>
                We serve both Saskatoon homeowners and businesses with comprehensive repair services:
              </p>
              <p>
                <strong>Residential Services:</strong> We repair garage doors on single-family homes, townhouses, and condos throughout Saskatoon. Our residential service includes spring replacement, opener repair, track alignment, panel replacement, and more. We understand how important your garage door is to your daily routine.
              </p>
              <p>
                <strong>Commercial Services:</strong> For Saskatoon businesses, we repair commercial overhead doors, rolling steel doors, high-speed doors, and loading dock equipment. We offer scheduled maintenance plans to minimize downtime and keep your business running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Problems */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Common Garage Door Problems We Fix</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {commonProblems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertTriangle size={24} className="text-orange-500 flex-shrink-0 mt-1" />
                      <h3 className="text-lg font-bold text-gray-900">{item.problem}</h3>
                    </div>
                    <p className="text-gray-700 ml-9">{item.solution}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Repair Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Our Repair Process</h2>
            <div className="space-y-6">
              {repairProcess.map((item) => (
                <Card key={item.step} className="border-l-4 border-blue-600">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-blue-600 text-white flex items-center justify-center text-2xl font-bold">
                          {item.step}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-700 text-lg">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Warranty Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Repair Warranty Coverage</h2>
            <Card className="border-2 border-blue-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Shield size={48} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">We Stand Behind Our Work</h3>
                    <p className="text-gray-700 text-lg mb-4">
                      Every repair performed by 306 Garage Doors comes with comprehensive warranty protection for Saskatoon homeowners and businesses.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {warrantyInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Garage Door Repair in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Don't wait for a small problem to become a major issue. Contact 306 Garage Doors for fast, professional repair service with warranty protection.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
              <Phone size={24} className="mr-2" />Call {businessInfo.phone}
            </Button>
            <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-8 text-xl">
              Request Free Quote
            </Button>
          </div>
          <p className="mt-6 text-blue-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default RepairPage;